import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { FieldCondition, Form, FormRenderProps, FormType } from '@enterprise/common';
import { Col, Row } from 'react-bootstrap';
import { SpotButton, SpotRadio, SpotRadioGroup } from '@enterprise/spot';
import { Field } from 'react-final-form';
import { MasterDataPageStore, SetupTypes } from '../MasterDataPageStore';
import { observer } from 'mobx-react';
import i18n from 'i18next';
import { ProTips } from '../../../components/helpers/ProTips';
import { MappingTypes } from '../../../core/enums/Mapping';
import { environment } from '../../../environment/environment';
import { useStores } from '../../../hooks';

interface PageProps {
    submit: (setupType) => void;
    // Unused by this component; kept optional so existing callers stay valid.
    page?: MasterDataPageStore;
}

interface ClassificationSetup {
    setupType: string | undefined;
}

export const ClassificationsSetupPage = observer(function ClassificationsSetupPageComponent(props: PageProps) {
    const { domain } = useStores();
    const store = domain.masterDataPageStore;

    const [selectedFile, setSelectedFile] = useState<File | null | undefined>(undefined);

    // Created once per mounted component, as the class instance fields were.
    const dataRef = useRef<ClassificationSetup>({ setupType: '' });
    const formRef = useRef<FormType<ClassificationSetup> | null>(null);
    if (formRef.current === null) {
        formRef.current = new FormType<ClassificationSetup>();
    }

    useEffect(() => {
        void store.loadMappingFileStatus(MappingTypes.CLASSIFICATION);

        return () => {
            store.stopListeningForMappingFileStatus();
        };
    }, [store]);

    const uploadMappingFile = (file: File) => {
        store.uploadClassificationsMappingFile(file).then((wasFileUploadedSuccessfully) => {
            setSelectedFile(undefined);
            if (wasFileUploadedSuccessfully) {
                store.startListeningForMappingFileStatus();
            }
        });
    };

    const submit = async ({ setupType }) => {
        dataRef.current.setupType = setupType;

        if (setupType !== SetupTypes.UPLOAD_FILE) {
            props.submit(setupType);
            return;
        }

        if (selectedFile) {
            await store.deleteExistingMappingFileStatus();
            uploadMappingFile(selectedFile);
        }
    };

    const onFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
        if (event.target?.files) {
            const file = event.target.files.item(0);
            setSelectedFile(file);
        }
    };

    const { isUploadingMappingFile, isProcessingMappingFile, mappingFileProcessingError } = store;

    if (isUploadingMappingFile || isProcessingMappingFile) {
        return <ProTips text={i18n.t('masterData:classificationsPage.processingYourFile', 'Processing your file')} />;
    }

    return (
        <>
            <Row style={{ marginTop: '70px' }}>
                <Col xs={1} />
                <Col xs={11}>
                    <h1 className="classifications-header">
                        <Trans i18nKey="masterData:classificationsPage.manageYourData">Manage your data across databases and clinics</Trans>
                    </h1>
                </Col>
            </Row>
            <Row>
                <Col xs={1} />
                <Col xs={6}>
                    <div className="classifications-details">
                        <Trans i18nKey="masterData:classificationsPage.details">
                            Choose an option below to set-up your master data set. The master set represents the goal state for the data within your
                            practices.
                        </Trans>
                    </div>
                </Col>
                <Col xs={5} />
            </Row>
            <Row style={{ marginTop: '30px' }}>
                <Col xs={1} />
                <Col xs={11}>
                    <Form formType={formRef.current} data={dataRef.current} onSubmit={submit}>
                        {({ handleSubmit }: FormRenderProps<ClassificationSetup>) => (
                            <>
                                <SpotRadioGroup>
                                    <Field name="setupType" type="radio" value={SetupTypes.OUR_CLASSIFICATIONS}>
                                        {({ input }) => (
                                            <SpotRadio {...input} value={SetupTypes.OUR_CLASSIFICATIONS}>
                                                <Trans i18nKey="masterData:classificationsPage.useOurMasterClassifications">
                                                    Use our master classifications
                                                </Trans>
                                            </SpotRadio>
                                        )}
                                    </Field>
                                    <Field name="setupType" type="radio" value={SetupTypes.UPLOAD_FILE}>
                                        {({ input }) => (
                                            <>
                                                <SpotRadio {...input} value={SetupTypes.UPLOAD_FILE}>
                                                    <Trans i18nKey="masterData:classificationsPage.uploadFile">Upload a file</Trans>
                                                    <a href={environment.MAPPING.classificationTemplateFileUrl} className="spot-link margin-left-2">
                                                        <Trans i18nKey="masterData:classificationsPage.downloadMasterDataTemplate">
                                                            (Download Master Data Template)
                                                        </Trans>
                                                    </a>
                                                </SpotRadio>

                                                <FieldCondition when="setupType" is={SetupTypes.UPLOAD_FILE}>
                                                    <input
                                                        accept={'.csv'}
                                                        onChange={onFileChange}
                                                        type="file"
                                                        className="spot-form__input mapping-file-input"
                                                    />

                                                    {mappingFileProcessingError && (
                                                        <span className="spot-form__field-error mapping-file-error">
                                                            <span className="spot-form__field-error-text">
                                                                <Trans
                                                                    i18nKey="masterData:classificationsPage.processingFileFailed"
                                                                    values={{ error: mappingFileProcessingError }}
                                                                />
                                                            </span>
                                                        </span>
                                                    )}
                                                </FieldCondition>
                                            </>
                                        )}
                                    </Field>
                                    <Field name="setupType" type="radio" value={SetupTypes.EXISTING_DATABASE}>
                                        {({ input }) => (
                                            <SpotRadio {...input} value={SetupTypes.EXISTING_DATABASE}>
                                                <Trans i18nKey="masterData:classificationsPage.useExistingDatabase">Use an existing database</Trans>
                                            </SpotRadio>
                                        )}
                                    </Field>
                                </SpotRadioGroup>

                                <SpotButton isPrimary={true} disabled={false} onClick={handleSubmit}>
                                    <Trans i18nKey="masterData:classificationsPage.setupMaster">Set up Master</Trans>
                                </SpotButton>
                            </>
                        )}
                    </Form>
                </Col>
            </Row>
        </>
    );
});
